import { UnitOfMeasureTypes } from 'src/common/enumerations/unit-of-measure-type';
import { getDefaultWeightUnitOfMeasure } from 'src/common/helpers/localized-description-helper';
import { EntryMethod } from '../../common/enumerations/entry-method';
import { validateVolume } from '../../common/helpers/volume-uom-validate';
import { validateWeight } from '../../common/helpers/weight-uom-validate';
import { IItemLookup } from '../../common/interfaces/dtos/IItemLookup';
import { IItemInputData } from '../../common/interfaces/IItemInputData';
import { LineItemAddDto } from './service/interfaces/line-item-add.dto';

export const getLineItem = (
  inputData: IItemInputData,
  unitOfMeasureCode?: string,
  weight?: number,
): LineItemAddDto => {
  if (!unitOfMeasureCode) {
    unitOfMeasureCode = inputData.isManualWeight
      ? getDefaultWeightUnitOfMeasure()
      : UnitOfMeasureTypes.Each;
  }
  if (!weight) {
    weight = inputData.isManualWeight ? inputData.quantity : 0;
  }

  const isItemWeightValid: boolean = validateWeight(unitOfMeasureCode);
  const isItemVolumeValid: boolean = validateVolume(unitOfMeasureCode);

  if (isItemVolumeValid || isItemWeightValid) {
    return getWeighableLineItem(unitOfMeasureCode, inputData, weight ?? 0);
  }
  return getNonWeighableLineItem(inputData, unitOfMeasureCode);
};

const getNonWeighableLineItem = (
  inputData: IItemInputData,
  unitOfMeasureCode: string,
): LineItemAddDto => {
  const quantityEntryMethod =
    inputData.entryMethod === EntryMethod.Repeat
      ? inputData.quantityEntryMethod
      : inputData.quantityEntryMethod || EntryMethod.Automatic;
  const lineItem: LineItemAddDto = {
    item: {
      id: inputData.itemId ?? '',
      entryMethod: inputData.entryMethod || EntryMethod.Automatic,
    },
    quantity: {
      value: inputData.quantity || 1,
      entryMethod: quantityEntryMethod,
    },
    allowDelayedRestriction: inputData.allowDelayedRestriction,
    scanDataType: inputData.scanDataType,
    uomCode: unitOfMeasureCode,
    withData: false,
    additionalData: inputData.additionalData,
  };
  return lineItem;
};

const getWeighableLineItem = (
  unitOfMeasureCode: string,
  inputData: IItemInputData,
  weight: number,
): LineItemAddDto => {
  const lineItem: LineItemAddDto = {
    item: {
      id: inputData.itemId ?? '',
      entryMethod: inputData.entryMethod || EntryMethod.Automatic,
    },
    quantity: {
      value: weight,
      entryMethod:
        inputData.isManualWeight || validateVolume(unitOfMeasureCode)
          ? EntryMethod.Keyed
          : EntryMethod.Automatic,
    },
    allowDelayedRestriction: inputData.allowDelayedRestriction,
    uomCode: unitOfMeasureCode,
    scanDataType: inputData.scanDataType,
    withData: false,
  };
  return lineItem;
};

export const getLinkedLineItems = (linkedItemsLookup: IItemLookup[]): LineItemAddDto[] => {
  return linkedItemsLookup.map(
    (linkedItem: IItemLookup): LineItemAddDto =>
      getNonWeighableLineItem(
        {
          entryMethod: EntryMethod.Tapped,
          itemId: linkedItem.itemId,
          quantityEntryMethod: EntryMethod.Automatic,
          manualQuantity: 0,
          quantity: 1,
        },
        linkedItem.unitsOfMeasure ?? '',
      ),
  );
};
